import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { Info } from 'lucide-react';
import { enqueueSnackbar } from '~/utils/notifications';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import type { InterpolationPreview } from '~/engine/util/algorithms/sdf/ContourOperations';
import { renderOutcomeMessage } from './interpolationOutcomeMessage';
import {
    InterpolationScope,
    renderPreviewMessage,
} from './interpolationPreviewMessage';
import { useInterpolationPreview } from './useInterpolationPreview';
import { editBlockedMessageId } from '~/components/Structures/editBlockedMessage';
import { useShownSlicePlaneOrRefusal } from '~/hooks/structure-set/useShownSlicePlaneOrRefusal';

type InterpolationMode = 'shapeOnly' | 'imageGuided';

// The segments share the narrow panel's width equally.
const toggleGroupSx = {
    '& .MuiToggleButton-root': { flex: 1, px: 0.75 },
};

interface InterpolationOperationProps {
    /** Dismisses the panel; called once an apply is verified complete. */
    onClose: () => void;
}

export default function InterpolationOperation({
    onClose,
}: InterpolationOperationProps) {
    const {
        environment,
        structureSet: currentStructureSet,
        roiNumber: selectedROI,
    } = useActiveEditTarget();
    const roi = environment?.structureSet?.getRoi(selectedROI);
    // Which guard (approval or the lock) blocks a contour edit; undefined
    // when the ROI is writable or absent. The single flag every gate below
    // reads, so the refusal always names the guard that actually applies.
    const editBlocker = roi?.contourEditBlocker;

    const [scope, setScope] = useState<InterpolationScope>('allEmpty');
    // Interpolation method. Defaults to the deterministic shape-only
    // blend; the image-intensity-aware (HU-guided) mode is opt-in and
    // stays non-default until the HU-guided path is clinically validated.
    const [mode, setMode] = useState<InterpolationMode>('shapeOnly');
    const imageGuided = mode === 'imageGuided';

    // The acquired slice a tile of the edit target shows. Subscribed, so
    // the preview read-out, on-image ghost, and Apply gating track the
    // slice as it scrolls; handleApply re-reads fresh via readPlaneOrRefuse
    // so a scroll between render and click cannot fill a different slice
    // than the one shown.
    const { plane, refusalMessageId, readPlaneOrRefuse } =
        useShownSlicePlaneOrRefusal('interpolateAllEmpty');
    const { preview, clearPreview } = useInterpolationPreview(
        scope,
        plane,
        imageGuided
    );
    // Gate Apply on both a fillable preview and a writable structure. An
    // APPROVED or locked ROI still produces a `fills` preview, so without
    // the blocker check the button would enable and only be rejected inside
    // handleApply; disabling up front avoids advertising an action that
    // cannot run. An absent roi has no blocker, which keeps the button
    // governed by the preview (its preview is `unavailable`, so it is
    // disabled anyway).
    const canInterpolate = preview.status === 'fills' && !editBlocker;

    // "Current" with no tile showing the acquired slice: the read-out names
    // that cause and its remedy instead of a fill read-out.
    const sliceRefused = scope === 'current' && plane === null;

    const isStructuresLoaded: undefined | boolean =
        !!currentStructureSet && selectedROI >= 0;

    const handleScopeChange = (
        _: React.MouseEvent<HTMLElement>,
        value: InterpolationScope | null
    ) => {
        if (value !== null) {
            setScope(value);
        }
    };

    const handleModeChange = (
        _: React.MouseEvent<HTMLElement>,
        value: InterpolationMode | null
    ) => {
        if (value !== null) {
            setMode(value);
        }
    };

    const handleApply = () => {
        if (editBlocker) {
            enqueueSnackbar(
                <FormattedMessage id={editBlockedMessageId(editBlocker)} />,
                { variant: 'error' }
            );
            return;
        }

        // Apply is gated on a fillable preview; a stale click landing while
        // the preview has not yet caught up to `fills` is a normal state
        // (the preview recomputes on its own), not a failure to report.
        if (preview.status !== 'fills') {
            return;
        }

        // This is the hazard: the button read as enabled but the edit
        // target (or the ROI within it) is gone by the time the click
        // lands. Silently no-op'ing here would look like a successful
        // interpolation.
        if (!environment || !currentStructureSet || !roi) {
            enqueueSnackbar(
                <FormattedMessage id="interpolationTargetUnavailable" />,
                { variant: 'error' }
            );
            return;
        }

        // Read at the click, never captured at render: the tile that showed
        // the slice may be gone or turned, and then the refusal is stated.
        // Refused before the preview is touched, so the ghost and read-out
        // stay with the preview, which follows the plane on its own.
        const shownPlane = scope === 'current' ? readPlaneOrRefuse() : null;
        if (scope === 'current' && shownPlane === null) {
            return;
        }

        // Remove the translucent ghost and un-dim the other structures before
        // committing, so the real (now-filled) contours render normally.
        clearPreview();

        const plannedSliceCount = preview.sliceCount;
        let postApplyPreview: InterpolationPreview;
        // Past the refusal above, only "All empty" has no plane.
        if (shownPlane === null) {
            environment.handleInterpolateAllSlices(roi, imageGuided);
            postApplyPreview = environment.previewInterpolateAllSlices(roi);
        } else {
            environment.handleInterpolateSingleSlice(
                roi,
                shownPlane,
                imageGuided
            );
            postApplyPreview = environment.previewInterpolateSingleSlice(
                roi,
                shownPlane
            );
        }

        // Post-apply verification: interpolation is clinically expected to
        // leave a completely filled contour, so the outcome — especially any
        // slice left unfilled (possible when a structure thinner than the SDF
        // falloff jumps a long way in-plane between slices, leaving the blend
        // of its interior below the iso threshold) — is reported explicitly,
        // never silently.
        const outcome = renderOutcomeMessage(
            plannedSliceCount,
            postApplyPreview,
            scope
        );
        enqueueSnackbar(outcome.message, { variant: outcome.severity });
        // The panel stays open on a gap or an unverified result, so the
        // error is read against the controls that produced it.
        if (outcome.severity === 'success') {
            onClose();
        }
    };

    if (!isStructuresLoaded) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="pleaseSelectStructure" />
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontSize={12}>
                        <FormattedMessage id="interpolation" />
                    </Typography>
                    <Tooltip
                        title={
                            <FormattedMessage id="imageGuidedInterpolationGuide" />
                        }
                        placement="top"
                    >
                        <Info size={14} />
                    </Tooltip>
                </Box>
                <ToggleButtonGroup
                    value={scope}
                    exclusive
                    onChange={handleScopeChange}
                    size="small"
                    sx={toggleGroupSx}
                >
                    <ToggleButton value="allEmpty">
                        <FormattedMessage id="allEmpty" />
                    </ToggleButton>
                    <ToggleButton value="current">
                        <FormattedMessage id="current" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                size="small"
                fullWidth
                sx={toggleGroupSx}
            >
                <ToggleButton value="shapeOnly">
                    <FormattedMessage id="interpolationShapeOnly" />
                </ToggleButton>
                <ToggleButton value="imageGuided">
                    <FormattedMessage id="interpolationImageGuided" />
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Show which slices the apply will fill, before the user commits. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    color: 'text.secondary',
                }}
            >
                <Info
                    size={12}
                    style={{ flexShrink: 0, marginTop: 2 }}
                    aria-hidden
                />
                <Typography
                    variant="caption"
                    sx={{ fontSize: 11, lineHeight: 1.3 }}
                    data-testid="interpolation-preview-text"
                >
                    {/* Explain WHY Apply is disabled — naming approval or the
                        lock, whichever applies — rather than showing a fill
                        read-out for an action that cannot run. */}
                    {editBlocker ? (
                        <FormattedMessage
                            id={editBlockedMessageId(editBlocker)}
                        />
                    ) : sliceRefused ? (
                        <FormattedMessage id={refusalMessageId} />
                    ) : (
                        renderPreviewMessage(preview, scope)
                    )}
                </Typography>
            </Box>
            <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={!canInterpolate}
                onClick={handleApply}
            >
                <FormattedMessage id="interpolate" />
            </Button>
        </Box>
    );
}
